import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * The camera, and the one interaction the tool rests on (§10.4).
 *
 * **Two decoders, and neither one is optional.** `BarcodeDetector` is native on
 * Android Chrome and iOS 17+, and where it exists it is the fast path: it
 * decodes a video frame directly, with no canvas round trip per frame, which is
 * what keeps intake's stick-scan-stick-scan loop at twenty parts in three
 * minutes. Everywhere else — desktop Safari and Firefox, and Chrome on Linux,
 * which is what the app is developed and demonstrated on — the constructor
 * simply is not there, so `jsQR` decodes from a canvas instead.
 *
 * §10.4 calls the JS decoder "the primary path, not a fallback", and the thing
 * it is guarding against is exactly what this hook used to do: gate the camera
 * itself on `BarcodeDetector`, so a browser without it never called
 * `getUserMedia`, never prompted for permission, and sat on "Camera off" with a
 * disabled button. The camera now starts on any browser that has one; only
 * *which decoder reads the frames* varies.
 *
 * `jsQR` is imported dynamically, so the phones that have the native detector
 * never download it — which is the "no library over workshop wifi" property the
 * native path was chosen for, kept without spending the desktop for it.
 *
 * The phone's own camera app also works, because the QR payload is a URL. This
 * hook exists for the multi-scan flows, where leaving the app between labels is
 * what makes twenty parts take twenty minutes instead of three.
 */

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<readonly { rawValue: string }[]>
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => BarcodeDetectorLike

function detectorCtor(): BarcodeDetectorCtor | null {
  const ctor = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector
  return typeof ctor === 'function' ? ctor : null
}

/** One frame in, a payload or null out. Both decoders are wrapped to this. */
type Decode = (video: HTMLVideoElement) => Promise<string | null>

function nativeDecoder(Ctor: BarcodeDetectorCtor): Decode {
  const detector = new Ctor({ formats: ['qr_code'] })
  return async (video) => (await detector.detect(video))[0]?.rawValue ?? null
}

/**
 * The canvas decoder. The frame is scaled to at most 640 px on its long edge
 * before `jsQR` sees it: a full 1280×720 decode costs 40-60 ms on a laptop and
 * the loop would spend all its time in it, while a 9 mm label held at 10-15 cm
 * still crosses far more than the ~100 px jsQR needs.
 */
async function canvasDecoder(): Promise<Decode> {
  const { default: jsQR } = await import('jsqr')
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  return async (video) => {
    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!ctx || vw === 0 || vh === 0) return null
    const scale = Math.min(1, 640 / Math.max(vw, vh))
    const w = Math.round(vw * scale)
    const h = Math.round(vh * scale)
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }
    ctx.drawImage(video, 0, 0, w, h)
    // `attemptBoth` reads inverted codes too — a white-on-black label, or one
    // photographed through a dark bag, costs nothing extra to accept.
    const code = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, {
      inversionAttempts: 'attemptBoth',
    })
    return code?.data ?? null
  }
}

export interface Scanner {
  readonly videoRef: React.RefObject<HTMLVideoElement>
  /** Whether this browser can open a camera at all. Not "has BarcodeDetector". */
  readonly supported: boolean
  /**
   * Why it cannot, as a sentence to put on screen — `null` when it can.
   *
   * A disabled button with no sentence beside it is the bug this whole hook was
   * rewritten for: the person is told the camera is off and nothing tells them
   * what to do about it. The commonest cause by far is not a missing camera but
   * a plain-HTTP origin, and *that* has a fix the person can act on, so it has
   * to be said out loud rather than expressed as a greyed-out control.
   */
  readonly unavailable: string | null
  readonly running: boolean
  /** True while the camera is opening — the permission prompt is on screen. */
  readonly starting: boolean
  readonly error: string | null
  start(): void
  stop(): void
}

function cameraSupported(): boolean {
  return typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia !== undefined
}

/** @see Scanner.unavailable */
function unavailableReason(): string | null {
  if (cameraSupported()) return null
  // `navigator.mediaDevices` is undefined outside a secure context, so a
  // browser with a perfectly good camera looks identical to one with none.
  // Reached over http://<lan-ip>:5175 that is *always* what has happened.
  return typeof window !== 'undefined' && window.isSecureContext === false
    ? 'The camera needs https or localhost. This page is plain http, so the browser will not open one — type the serial instead.'
    : 'This device has no camera Kronos can open. Type the serial instead.'
}

/**
 * @param onScan called with the raw payload of each distinct code seen. The
 *   same code is not reported twice in a row: a QR stays in frame for many
 *   frames, and firing per frame would file twenty squawks.
 */
export function useScanner(onScan: (raw: string) => void): Scanner {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const frameRef = useRef<number | null>(null)
  const lastRef = useRef<string | null>(null)
  /**
   * Bumped by every `start` and every `stop`, and re-checked after each await.
   *
   * `getUserMedia` takes long enough to be torn down mid-flight, and React 18's
   * StrictMode does exactly that on every mount in development: start, stop,
   * start. Without this the first call resolves *after* its own `stop()`, hands
   * back a live stream that overwrites the second one's, and the camera light
   * stays on with nothing holding the reference.
   */
  const genRef = useRef(0)
  // Held in a ref so restarting the loop is not a dependency of every render.
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  const [running, setRunning] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const supported = cameraSupported()
  const unavailable = unavailableReason()

  const stop = useCallback(() => {
    genRef.current += 1
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = null
    // Every track, explicitly. Leaving one live keeps the camera indicator on,
    // which people reasonably read as the app watching them.
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    lastRef.current = null
    setRunning(false)
    setStarting(false)
  }, [])

  const start = useCallback(() => {
    const why = unavailableReason()
    if (why) {
      setError(why)
      return
    }
    setError(null)
    setStarting(true)
    const gen = (genRef.current += 1)
    const stale = (): boolean => genRef.current !== gen
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // The back camera where there is one — `ideal`, not `exact`, so a
          // laptop with only a front camera gets that rather than an error.
          // A resolution high enough that a 9 mm code resolves at 10-15 cm.
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
        })
        const video = videoRef.current
        if (stale() || !video) {
          stream.getTracks().forEach((t) => t.stop())
          if (!stale()) setStarting(false)
          return
        }
        streamRef.current = stream
        video.srcObject = stream
        await video.play()

        const Ctor = detectorCtor()
        const decode = Ctor ? nativeDecoder(Ctor) : await canvasDecoder()
        // `stop()` may have run while jsQR was downloading.
        if (stale() || !streamRef.current) return
        setStarting(false)
        setRunning(true)

        const tick = async (): Promise<void> => {
          if (stale() || !streamRef.current) return
          try {
            const raw = await decode(video)
            if (raw && raw !== lastRef.current) {
              lastRef.current = raw
              onScanRef.current(raw)
            } else if (!raw) {
              // Cleared once the code leaves frame, so scanning the *same* part
              // twice on purpose works.
              lastRef.current = null
            }
          } catch {
            // A single dropped frame is not worth stopping the camera for.
          }
          if (stale() || !streamRef.current) return
          frameRef.current = requestAnimationFrame(() => void tick())
        }
        frameRef.current = requestAnimationFrame(() => void tick())
      } catch (e) {
        if (stale()) return
        const err = e as Error
        setError(
          err.name === 'NotAllowedError'
            ? 'Camera access was refused. Allow it in the browser settings, or type the serial.'
            : err.name === 'NotFoundError'
              ? 'No camera on this device. Type the serial instead.'
              : `Could not start the camera: ${err.message}`,
        )
        stop()
      }
    })()
  }, [stop])

  // Release the camera when the screen goes away, whatever the reason.
  useEffect(() => stop, [stop])

  return { videoRef, supported, unavailable, running, starting, error, start, stop }
}
